import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { fatcaUpload } from "../../../api/bseApi";

const OCCUPATION_CODES = [
  { value: "01", label: "Business" },
  { value: "02", label: "Services" },
  { value: "03", label: "Professional" },
  { value: "04", label: "Agriculture" },
  { value: "05", label: "Retired" },
  { value: "06", label: "Housewife" },
  { value: "07", label: "Student" },
  { value: "08", label: "Others" },
];

const SOURCE_OF_WEALTH_CODES = [
  { value: "01", label: "Salary" },
  { value: "02", label: "Business" },
  { value: "03", label: "Inheritance" },
  { value: "04", label: "Investments" },
  { value: "05", label: "Rental Income" },
  { value: "06", label: "Pension" },
  { value: "07", label: "Others" },
];

const INCOME_SLAB_CODES = [
  { value: "01", label: "Up to 1 Lac" },
  { value: "02", label: "1 Lac - 5 Lac" },
  { value: "03", label: "5 Lac - 10 Lac" },
  { value: "04", label: "10 Lac - 25 Lac" },
  { value: "05", label: "25 Lac - 50 Lac" },
  { value: "06", label: "50 Lac - 1 Crore" },
  { value: "07", label: "Above 1 Crore" },
];

const COUNTRY_CODES = [
  { value: "IN", label: "India" },
  { value: "US", label: "United States" },
  { value: "UK", label: "United Kingdom" },
  { value: "AE", label: "UAE" },
  { value: "SG", label: "Singapore" },
  { value: "OTHER", label: "Other" },
];

const initialForm = {
  countryCode: "IN",
  occupationCode: "",
  sourceOfWealth: "",
  applicantIncome: "",
  taxResidencyCountry: "IN",
  uboFlag: "N",
  uboCode: "",
};

/**
 * FatcaDetailsScreen - BSE StAR MF FATCA Upload
 * Opens either:
 * 1. After successful UCC creation (location.state: clientCode, PAN, clientName, DOB, taxStatus)
 * 2. From User Listing via /user/fatca/:userId (fetches user, uses ucc as clientCode)
 */
export default function FatcaDetailsScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(!!userId);
  const [errors, setErrors] = useState({});
  const [context, setContext] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    const state = location.state;

    // Flow 1: From UCC creation - state has clientCode and PAN
    if (state?.clientCode && state?.PAN) {
      setContext({
        clientCode: state.clientCode,
        PAN: state.PAN,
        clientName: state.clientName || "",
        DOB: state.DOB || "",
        taxStatus: state.taxStatus || "R",
      });
      setFetching(false);
      return;
    }

    // Flow 2: From User Listing - userId in URL, fetch user
    if (userId) {
      const fetchUser = async () => {
        setFetching(true);
        setErrorMsg(null);
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_BASE_URL}/user/${userId}`
          );
          const user = res.data?.status === 1 ? res.data?.data : null;
          if (!user) {
            setErrorMsg(res.data?.error || "User not found.");
            setFetching(false);
            return;
          }
          const ucc = user.ucc ?? user.UCC ?? "";
          const pan = user.pan_number ?? user.pan ?? "";
          const uccValid = ucc && String(ucc).trim() !== "" && String(ucc) !== "Reject";
          if (!uccValid || !pan) {
            setErrorMsg("Missing required data. Please complete UCC creation first.");
            setFetching(false);
            return;
          }
          const clientName = [user.first_name, user.last_name].filter(Boolean).join(" ") || "";
          const dob = user.date_of_birth ?? user.dob ?? "";
          const taxStatus = user.tax_status ?? user.residential_status ?? "R";
          const taxMap = { R: "R", N: "N", P: "P", C: "C", T: "T" };
          const tax = taxMap[taxStatus] ?? (String(taxStatus || "").toUpperCase().includes("NON") ? "N" : "R");
          setContext({
            clientCode: String(ucc).trim(),
            PAN: String(pan).trim(),
            clientName,
            DOB: dob,
            taxStatus: tax,
          });
        } catch (err) {
          setErrorMsg(err.response?.data?.error || err.message || "Failed to load user.");
        } finally {
          setFetching(false);
        }
      };
      fetchUser();
      return;
    }

    // No state and no userId - show error
    setErrorMsg("Missing required data. Please complete UCC creation first or open from User Listing.");
    setFetching(false);
  }, [location.state, userId, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let v = value;
    if (name === "uboFlag") v = value.toUpperCase();
    setForm((prev) => ({ ...prev, [name]: v }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const err = {};
    if (!form.countryCode?.trim()) err.countryCode = "Required";
    if (!form.occupationCode?.trim()) err.occupationCode = "Required";
    if (!form.sourceOfWealth?.trim()) err.sourceOfWealth = "Required";
    if (!form.applicantIncome?.trim()) err.applicantIncome = "Required";
    if (form.uboFlag === "Y" && !form.uboCode?.trim()) {
      err.uboCode = "UBO Code is mandatory when UBO Flag is Y";
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate() || !context) return;

    setLoading(true);
    try {
      const payload = {
        clientCode: context.clientCode,
        PAN: context.PAN,
        clientName: context.clientName,
        DOB: context.DOB,
        taxStatus: context.taxStatus,
        countryCode: form.countryCode,
        occupationCode: form.occupationCode,
        sourceOfWealth: form.sourceOfWealth,
        applicantIncome: form.applicantIncome,
        taxResidencyCountry: form.taxResidencyCountry,
        uboFlag: form.uboFlag,
        uboCode: form.uboFlag === "Y" ? form.uboCode : "",
      };

      const res = await fatcaUpload(payload);

      if (res.data?.status === 1) {
        const bseFailed = res.data?.bseFailed === true;
        Swal.fire({
          icon: "success",
          title: "FATCA Saved",
          text: bseFailed
            ? res.data?.message || "FATCA details saved. BSE update failed; you can retry later."
            : (res.data?.message || "FATCA details submitted successfully."),
        });
        navigate("/user/ckyc", {
          state: {
            clientCode: context.clientCode,
            PAN: context.PAN,
            clientName: context.clientName,
          },
        });
      } else {
        const msg = res.data?.bseMessage || res.data?.message || "FATCA upload failed";
        Swal.fire("Error", msg, "error");
      }
    } catch (err) {
      const d = err.response?.data;
      const msg =
        (typeof d?.message === "string" && d.message) ||
        (typeof d?.bseMessage === "string" && d.bseMessage) ||
        (typeof d?.error === "string" && d.error) ||
        (typeof d === "string" && d) ||
        (err.response?.status === 400 && "Invalid request. Check client code and PAN, or try again.") ||
        (err.response?.status >= 500 && "Server error. Try again or contact support.") ||
        err.message ||
        "FATCA upload failed";
      Swal.fire("Error", msg, "error");
    } finally {
      setLoading(false);
    }
  };

  if (errorMsg) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <p className="text-danger mb-3">{errorMsg}</p>
              <Link to="/user" className="btn btn-primary">Back to Users</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (fetching || !context) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "20px",
            }}
          >
            <nav aria-label="breadcrumb" style={{ marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link> </li>
                <li className="breadcrumb-item"><Link to="/user">Users</Link> </li>
                <li className="breadcrumb-item"><Link to="/user">UCC</Link> </li>
                <li className="breadcrumb-item active">FATCA Details</li>
              </ol>
            </nav>
            <h5>FATCA Details</h5>
            <p className="text-muted mb-0">
              Client: {context.clientName} | PAN: {context.PAN} | Client Code: {context.clientCode}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div
              className="mb-4"
              style={{
                background: "#ffffff",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                padding: "24px",
                border: "1px solid #e6eef0",
              }}
            >
              <h6 className="mb-4" style={{ color: "#0b6f68", fontWeight: "600" }}>
                FATCA Details
              </h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Country (CountryCode) *</label>
                  <select
                    className={`form-select ${errors.countryCode ? "is-invalid" : ""}`}
                    name="countryCode"
                    value={form.countryCode}
                    onChange={handleChange}
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                  {errors.countryCode && <div className="invalid-feedback">{errors.countryCode}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Occupation (OccupationCode) *</label>
                  <select
                    className={`form-select ${errors.occupationCode ? "is-invalid" : ""}`}
                    name="occupationCode"
                    value={form.occupationCode}
                    onChange={handleChange}
                  >
                    <option value="">Select</option>
                    {OCCUPATION_CODES.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  {errors.occupationCode && <div className="invalid-feedback">{errors.occupationCode}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Source of Wealth *</label>
                  <select
                    className={`form-select ${errors.sourceOfWealth ? "is-invalid" : ""}`}
                    name="sourceOfWealth"
                    value={form.sourceOfWealth}
                    onChange={handleChange}
                  >
                    <option value="">Select</option>
                    {SOURCE_OF_WEALTH_CODES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                  {errors.sourceOfWealth && <div className="invalid-feedback">{errors.sourceOfWealth}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Applicant Income (Income Slab) *</label>
                  <select
                    className={`form-select ${errors.applicantIncome ? "is-invalid" : ""}`}
                    name="applicantIncome"
                    value={form.applicantIncome}
                    onChange={handleChange}
                  >
                    <option value="">Select</option>
                    {INCOME_SLAB_CODES.map((i) => (
                      <option key={i.value} value={i.value}>{i.label}</option>
                    ))}
                  </select>
                  {errors.applicantIncome && <div className="invalid-feedback">{errors.applicantIncome}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Tax Residency Country</label>
                  <select
                    className="form-select"
                    name="taxResidencyCountry"
                    value={form.taxResidencyCountry}
                    onChange={handleChange}
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">UBO Flag</label>
                  <select
                    className="form-select"
                    name="uboFlag"
                    value={form.uboFlag}
                    onChange={handleChange}
                  >
                    <option value="Y">Y</option>
                    <option value="N">N</option>
                  </select>
                </div>
                {form.uboFlag === "Y" && (
                  <div className="col-md-6">
                    <label className="form-label">UBO Code *</label>
                    <input
                      type="text"
                      className={`form-control ${errors.uboCode ? "is-invalid" : ""}`}
                      name="uboCode"
                      value={form.uboCode}
                      onChange={handleChange}
                      placeholder="Mandatory when UBO Flag = Y"
                    />
                    {errors.uboCode && <div className="invalid-feedback">{errors.uboCode}</div>}
                  </div>
                )}
              </div>

              <div className="d-flex gap-2 justify-content-end mt-4 pt-3" style={{ borderTop: "1px solid #e6eef0" }}>
                <Link to="/user" className="btn btn-secondary">
                  Cancel
                </Link>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      Submitting...
                    </>
                  ) : (
                    "Submit FATCA"
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
